import { create } from 'zustand';
import { supabase } from '../lib/supabase';
import { useAuthStore } from './authStore';
import type { Team, Sport } from '../types';

interface TeamState {
  currentTeam: Team | null;
  allTeams: Team[];
  loading: boolean;
  error: string | null;
  createTeam: (team: Omit<Team, 'id' | 'players'>) => Promise<void>;
  setCurrentTeam: (team: Team | null) => void;
  updateTeam: (team: Partial<Team>) => Promise<void>;
  deleteTeam: (teamId: string) => Promise<void>;
  fetchAllTeams: () => Promise<void>;
  switchTeam: (teamId: string) => Promise<void>;
}

export const useTeamStore = create<TeamState>((set, get) => ({
  currentTeam: null,
  allTeams: [],
  loading: false,
  error: null,
  
  fetchAllTeams: async () => {
    set({ loading: true, error: null });
    try {
      const { user, isAuthenticated } = useAuthStore.getState();
      
      if (!isAuthenticated || !user) {
        set({ loading: false });
        return;
      }

      // Use a simplified query to avoid any policy issues
      // First get owned teams
      const { data: ownedTeams, error: ownedError } = await supabase
        .from('teams')
        .select('*')
        .eq('user_id', user.id);

      if (ownedError) throw ownedError;

      // Then get coach teams separately
      const { data: coachTeams, error: coachError } = await supabase
        .from('coaches')
        .select('team_id')
        .eq('user_id', user.id);

      if (coachError) throw coachError;

      // Get the actual team data for coach teams
      let coachTeamData: any[] = [];
      if (coachTeams && coachTeams.length > 0) {
        const teamIds = coachTeams.map(ct => ct.team_id);
        const { data: coachTeamDetails, error: coachTeamDetailsError } = await supabase
          .from('teams')
          .select('*')
          .in('id', teamIds);

        if (coachTeamDetailsError) throw coachTeamDetailsError;
        coachTeamData = coachTeamDetails || [];
        }

      // Combine and deduplicate teams
      const allTeams = [...(ownedTeams || []), ...coachTeamData];
      const uniqueTeams = Array.from(
        new Map(allTeams.map(team => [team.id, team])).values()
      );

      // Transform the response from snake_case to camelCase
      const transformedTeams: Team[] = uniqueTeams.map((teamData: any) => {
        
        const camelCaseTeam: Team = {
          id: teamData.id,
          name: teamData.name,
          sport: teamData.sport as Sport,
          ageLevel: teamData.age_level,
          experienceLevel: teamData.experience_level,
          rosterSize: teamData.roster_size,
          format: teamData.format,
          defaultPracticeStartTime: teamData.default_practice_start_time,
          defaultPracticeEndTime: teamData.default_practice_end_time,
          defaultPracticeLocation: teamData.default_practice_location,
          defaultDrillStepsPerDrill: teamData.default_drill_steps_per_drill,
          aiUsePositionGroups: teamData.ai_use_position_groups ?? true,
          timeFormat: teamData.time_format,
          playerSortBy: teamData.player_sort_by,
          enableRandomization: Boolean(teamData.enable_randomization),
          isPersonal: Boolean(teamData.is_personal),
          created_at: teamData.created_at,
          updated_at: teamData.updated_at
        };
        
        // Add sport-specific fields based on the sport
        if (teamData.sport === 'Football' || 
            teamData.sport === 'Basketball' || 
            teamData.sport === 'Soccer' ||
            teamData.sport === 'Volleyball' ||
            teamData.sport === 'Lacrosse') {
          camelCaseTeam.offensiveScheme = teamData.offensive_scheme;
          camelCaseTeam.defensiveScheme = teamData.defensive_scheme;
        } else if (teamData.sport === 'Baseball' ||
                   teamData.sport === 'Softball' ||
                   teamData.sport === 'Hockey' ||
                   teamData.sport === 'Tennis') {
          camelCaseTeam.offensiveScheme = teamData.offensive_scheme;
          if (teamData.sport === 'Baseball' || teamData.sport === 'Softball') {
            camelCaseTeam.innings = teamData.innings;
          }
        }
        
        // Add default formation fields for all sports
        camelCaseTeam.defaultOffenseFormation = teamData.default_offense_formation;
        camelCaseTeam.defaultDefenseFormation = teamData.default_defense_formation;
        camelCaseTeam.defaultSpecialTeamsFormation = teamData.default_special_teams_formation;
        camelCaseTeam.defaultLineupFormation = teamData.default_lineup_formation;
        camelCaseTeam.defaultFieldingFormation = teamData.default_fielding_formation;
        
        return camelCaseTeam;
      });
      
      set({ allTeams: transformedTeams });
      
      // Get saved team ID from localStorage
      const savedTeamId = localStorage.getItem('selectedTeamId');
      
      // Try to find the saved team in our loaded teams
      if (savedTeamId) {
        const savedTeam = transformedTeams.find(team => team.id === savedTeamId);
        if (savedTeam) {
          set({ currentTeam: savedTeam });
          return;
        }
      }
      
      // If no saved team or saved team not found, use first team
      if (transformedTeams.length > 0) {
        set({ currentTeam: transformedTeams[0] });
        // Update localStorage
        localStorage.setItem('selectedTeamId', transformedTeams[0].id);
      }
    } catch (error: any) {
      console.error('[teamStore] Error fetching teams:', error);
      set({ error: error.message });
    } finally {
      set({ loading: false });
    }
  },
  
  switchTeam: async (teamId: string) => {
    const { allTeams, currentTeam } = get();
    const selectedTeam = allTeams.find(team => team.id === teamId);
    
    if (selectedTeam) {
      // Check if this is actually a team change
      if (!currentTeam || currentTeam.id !== selectedTeam.id) {
        
        // Set the current team in the store
        set({ currentTeam: selectedTeam });
        
        // Update localStorage
        localStorage.setItem('selectedTeamId', selectedTeam.id);
        
        // Dispatch cache clear event first to ensure all caches are cleared
        const cacheClearEvent = new CustomEvent('clear-team-cache', {
          detail: { newTeamId: selectedTeam.id }
        });
        window.dispatchEvent(cacheClearEvent);
        
        // Then dispatch team change event
        const teamChangeEvent = new CustomEvent('team-changed', { 
          detail: { teamId: selectedTeam.id, name: selectedTeam.name }
        });
        window.dispatchEvent(teamChangeEvent);
      }
    } else {
      console.error(`[teamStore] Team with ID ${teamId} not found`);
      set({ error: 'Team not found' });
    }
  },
  
  createTeam: async (team) => {
    set({ loading: true, error: null });
    try {
      const { user } = useAuthStore.getState();
      if (!user) throw new Error('No user found');

      // Transform the field names to match the database schema
      const transformedTeam: Record<string, any> = {
        name: team.name,
        sport: team.sport,
        age_level: team.ageLevel,
        experience_level: team.experienceLevel,
        roster_size: team.rosterSize,
        format: team.format,
        user_id: user.id,
        default_practice_start_time: team.defaultPracticeStartTime,
        default_practice_end_time: team.defaultPracticeEndTime,
        default_practice_location: team.defaultPracticeLocation,
        default_drill_steps_per_drill: team.defaultDrillStepsPerDrill ?? 3,
        ai_use_position_groups: team.aiUsePositionGroups ?? true,
        time_format: team.timeFormat,
        player_sort_by: team.playerSortBy,
        enable_randomization: team.enableRandomization,
        is_personal: team.isPersonal ?? false
      };
      
      // Add sport-specific fields based on the selected sport
      if (team.sport === 'Football') {
        transformedTeam.offensive_scheme = team.offensiveScheme;
        transformedTeam.defensive_scheme = team.defensiveScheme;
      } else if (team.sport === 'Basketball') {
        transformedTeam.offensive_scheme = team.offensiveScheme;
        transformedTeam.defensive_scheme = team.defensiveScheme;
      } else if (team.sport === 'Soccer') {
        transformedTeam.offensive_scheme = team.offensiveScheme;
        transformedTeam.defensive_scheme = team.defensiveScheme;
      } else if (team.sport === 'Baseball' || team.sport === 'Softball') {
        transformedTeam.offensive_scheme = team.offensiveScheme;
        transformedTeam.innings = team.innings;
      } else if (team.sport === 'Hockey') {
        transformedTeam.offensive_scheme = team.offensiveScheme;
      } else if (team.sport === 'Volleyball') {
        transformedTeam.offensive_scheme = team.offensiveScheme;
        transformedTeam.defensive_scheme = team.defensiveScheme;
      } else if (team.sport === 'Lacrosse') {
        transformedTeam.offensive_scheme = team.offensiveScheme;
        transformedTeam.defensive_scheme = team.defensiveScheme;
      } else if (team.sport === 'Tennis') {
        transformedTeam.offensive_scheme = team.offensiveScheme;
      }

      const { data, error } = await supabase
        .from('teams')
        .insert([transformedTeam])
        .select()
        .single();

      if (error) throw error;
      
      // Transform the response back to camelCase for the frontend
      const camelCaseTeam: Team = {
        id: data.id,
        name: data.name,
        sport: data.sport as Sport,
        ageLevel: data.age_level,
        experienceLevel: data.experience_level,
        rosterSize: data.roster_size,
        format: data.format,
        defaultPracticeStartTime: data.default_practice_start_time,
        defaultPracticeEndTime: data.default_practice_end_time,
        defaultPracticeLocation: data.default_practice_location,
        defaultDrillStepsPerDrill: data.default_drill_steps_per_drill,
        aiUsePositionGroups: data.ai_use_position_groups ?? true,
        timeFormat: data.time_format,
        playerSortBy: data.player_sort_by,
        enableRandomization: data.enable_randomization,
        isPersonal: data.is_personal ?? false,
        players: []  // Add an empty players array that will be populated later
      };
      
      // Add sport-specific fields from the database response
      if (data.sport === 'Football' || 
          data.sport === 'Basketball' || 
          data.sport === 'Soccer' ||
          data.sport === 'Volleyball' ||
          data.sport === 'Lacrosse') {
        camelCaseTeam.offensiveScheme = data.offensive_scheme;
        camelCaseTeam.defensiveScheme = data.defensive_scheme;
      } else if (data.sport === 'Baseball' ||
                data.sport === 'Softball' ||
                data.sport === 'Hockey' ||
                data.sport === 'Tennis') {
        camelCaseTeam.offensiveScheme = data.offensive_scheme;
        if (data.sport === 'Baseball' || data.sport === 'Softball') {
          camelCaseTeam.innings = data.innings;
        }
      }
      
      // Add default formation fields for all sports
      camelCaseTeam.defaultOffenseFormation = data.default_offense_formation;
      camelCaseTeam.defaultDefenseFormation = data.default_defense_formation;
      camelCaseTeam.defaultSpecialTeamsFormation = data.default_special_teams_formation;
      camelCaseTeam.defaultLineupFormation = data.default_lineup_formation;
      camelCaseTeam.defaultFieldingFormation = data.default_fielding_formation;
      
      // Update the store
      set({ 
        currentTeam: camelCaseTeam, 
        allTeams: [...get().allTeams, camelCaseTeam] 
      });
      
      // Update localStorage
      localStorage.setItem('selectedTeamId', camelCaseTeam.id);
      
      // Dispatch cache clear event first to ensure all caches are cleared
      const cacheClearEvent = new CustomEvent('clear-team-cache', {
        detail: { newTeamId: camelCaseTeam.id }
      });
      window.dispatchEvent(cacheClearEvent);
      
      // Then dispatch team change event
      const teamChangeEvent = new CustomEvent('team-changed', { 
        detail: { teamId: camelCaseTeam.id, name: camelCaseTeam.name }
      });
      window.dispatchEvent(teamChangeEvent);
    } catch (error: any) {
      set({ error: error.message });
      throw error; // Re-throw the error so the component can handle it
    } finally {
      set({ loading: false });
    }
  },
  
  setCurrentTeam: (team) => {
    if (team) {
      set({ currentTeam: team });
      
      // Update localStorage
      localStorage.setItem('selectedTeamId', team.id);
      
      // Dispatch cache clear event first to ensure all caches are cleared
      const cacheClearEvent = new CustomEvent('clear-team-cache', {
        detail: { newTeamId: team.id }
      });
      window.dispatchEvent(cacheClearEvent);
      
      // Then dispatch team change event
      const teamChangeEvent = new CustomEvent('team-changed', { 
        detail: { teamId: team.id, name: team.name }
      });
      window.dispatchEvent(teamChangeEvent);
    } else {
      set({ currentTeam: null });
      localStorage.removeItem('selectedTeamId');
      
      // Dispatch a custom event to notify other components of team change
      const teamChangeEvent = new CustomEvent('team-changed', { 
        detail: { teamId: null, name: null }
      });
      window.dispatchEvent(teamChangeEvent);
    }
  },
  
  updateTeam: async (team) => {
    set({ loading: true, error: null });
    try {
      // Transform the field names to match the database schema
      const transformedTeam: Record<string, any> = {
        name: team.name,
        sport: team.sport,
        age_level: team.ageLevel,
        experience_level: team.experienceLevel,
        roster_size: team.rosterSize,
        format: team.format,
        default_practice_start_time: team.defaultPracticeStartTime,
        default_practice_end_time: team.defaultPracticeEndTime,
        default_practice_location: team.defaultPracticeLocation,
        default_drill_steps_per_drill: team.defaultDrillStepsPerDrill,
        ai_use_position_groups: team.aiUsePositionGroups ?? true,
        time_format: team.timeFormat,
        player_sort_by: team.playerSortBy,
        enable_randomization: team.enableRandomization
      };
      
      // Add sport-specific fields based on the selected sport
      if (team.sport === 'Football') {
        transformedTeam.offensive_scheme = team.offensiveScheme;
        transformedTeam.defensive_scheme = team.defensiveScheme;
      } else if (team.sport === 'Basketball') {
        transformedTeam.offensive_scheme = team.offensiveScheme;
        transformedTeam.defensive_scheme = team.defensiveScheme;
      } else if (team.sport === 'Soccer') {
        transformedTeam.offensive_scheme = team.offensiveScheme;
        transformedTeam.defensive_scheme = team.defensiveScheme;
      } else if (team.sport === 'Baseball' || team.sport === 'Softball') {
        transformedTeam.offensive_scheme = team.offensiveScheme;
        transformedTeam.innings = team.innings;
      } else if (team.sport === 'Hockey') {
        transformedTeam.offensive_scheme = team.offensiveScheme;
      } else if (team.sport === 'Volleyball') {
        transformedTeam.offensive_scheme = team.offensiveScheme;
        transformedTeam.defensive_scheme = team.defensiveScheme;
      } else if (team.sport === 'Lacrosse') {
        transformedTeam.offensive_scheme = team.offensiveScheme;
        transformedTeam.defensive_scheme = team.defensiveScheme;
      } else if (team.sport === 'Tennis') {
        transformedTeam.offensive_scheme = team.offensiveScheme;
      }

      // Add default formation fields (only include if they exist in the team object)
      if (team.defaultOffenseFormation !== undefined) {
        transformedTeam.default_offense_formation = team.defaultOffenseFormation;
      }
      if (team.defaultDefenseFormation !== undefined) {
        transformedTeam.default_defense_formation = team.defaultDefenseFormation;
      }
      if (team.defaultSpecialTeamsFormation !== undefined) {
        transformedTeam.default_special_teams_formation = team.defaultSpecialTeamsFormation;
      }
      if (team.defaultLineupFormation !== undefined) {
        transformedTeam.default_lineup_formation = team.defaultLineupFormation;
      }
      if (team.defaultFieldingFormation !== undefined) {
        transformedTeam.default_fielding_formation = team.defaultFieldingFormation;
      }

      const { error } = await supabase
        .from('teams')
        .update(transformedTeam)
        .eq('id', get().currentTeam?.id);

      if (error) {
        throw error;
      }
      
      // Update the store with the new team data
      const updatedTeam = { ...get().currentTeam, ...team } as Team;
      
      // Update both currentTeam and the team in allTeams array
      const { allTeams } = get();
      const updatedAllTeams = allTeams.map(t => 
        t.id === updatedTeam.id ? updatedTeam : t
      );
      
      set({ 
        currentTeam: updatedTeam,
        allTeams: updatedAllTeams
      });
      
      // Update localStorage in case the ID changed
      localStorage.setItem('selectedTeamId', updatedTeam.id);
      
      // Dispatch a custom event to notify other components of team change
      const teamChangeEvent = new CustomEvent('team-changed', { 
        detail: { teamId: updatedTeam.id, name: updatedTeam.name }
      });
      window.dispatchEvent(teamChangeEvent);
    } catch (error: any) {
      console.error('[teamStore] Error updating team:', error);
      set({ error: error.message });
    } finally {
      set({ loading: false });
    }
  },

  deleteTeam: async (teamId: string) => {
    set({ loading: true, error: null });
    try {
      const { currentTeam, allTeams } = get();
      const teamToDelete = allTeams.find(team => team.id === teamId);
      
      if (!teamToDelete) {
        throw new Error('Team not found');
      }

      // Delete the team from the database
      const { error } = await supabase
        .from('teams')
        .delete()
        .eq('id', teamId);

      if (error) throw error;
      
      // Remove the team from the allTeams array
      const updatedTeams = allTeams.filter(team => team.id !== teamId);
      set({ allTeams: updatedTeams });
      
      // If the deleted team was the current team, switch to another team or clear current team
      if (currentTeam?.id === teamId) {
        set({ currentTeam: null });
        localStorage.removeItem('selectedTeamId');
      }
    } catch (error: any) {
      console.error('[teamStore] Error deleting team:', error);
      set({ error: error.message });
    } finally {
      set({ loading: false });
    }
  },
}));

// Remove the global event listener that's causing infinite loops
// Components should handle their own refresh logic
// Listen for force refresh events
// if (typeof window !== 'undefined') {
//   window.addEventListener('force-team-refresh', () => {
//     console.log('[teamStore] Received force-team-refresh event, refreshing teams...');
//     useTeamStore.getState().fetchAllTeams();
//   });
// }